import { type ChangeEvent, useEffect, useRef, useState } from 'react'
import { debounce } from 'lodash'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { Button, IconButton, Tooltip } from 'shared/ui'
import {
  makeTextFieldAutoFocus,
  makeTextFieldElement,
  makeTextFieldIcon,
  TextField,
} from 'shared/ui/TextField'
import { type MergeField } from 'entities/MergeField/model/MergeField'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useMediaLibraryContext } from 'widgets/MediaLibrary/context/mediaLibraryContext'
import { SizeLimitsTooltip } from 'widgets/MessageField'
import { MergeFieldUrlDropdown } from 'widgets/MergeField/ui/actions/MergeFieldUrlDropdown/MergeFieldUrlDropdown'
import { MergeFieldsStore } from 'widgets/MergeField'
import { getPicsnippetMergeFieldLabel, isPicsnippetsUrl } from 'widgets/MediaLibrary/utils'
import { RinglessSelectInfoTooltip } from 'widgets/MediaLibrary/ui/MediaLibraryModal/MediaLibraryInfoTooltips/RinglessSelectInfoTooltip'
import styles from './styles.module.scss'

export const MediaLibraryControls = observer(() => {
  const entities = useEntitiesContext()
  const { featureFlagsStore, attachmentStore } = entities
  const mediaLibraryStore = useMediaLibraryContext()
  const {
    toggleViewType,
    addAttachmentViaUrl,
    selectedViewType,
    searchTerm,
    setSearchTerm,
    isSearchResettingForUpload,
    isAnyFileWaitingForUpload,
    isConversationMessageField,
    conversationContact,
    isAircallNumber,
    isAttachmentInitListLoading,
    setError,
    isRinglessSelectVariant,
    isAudioSelectVariant,
    isAudioDurationLimitEnabled,
    isDefaultVariant,
    uploading,
  } = mediaLibraryStore
  const ref = useRef<HTMLInputElement>(null)
  const textRef = useRef<HTMLInputElement>(null)
  const [isUploadViaUrlOpen, setIsUploadViaUrlOpen] = useState(false)
  const [uploadUrl, setUploadUrl] = useState('')
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [inputSearchTerm, setInputSearchTerm] = useState(searchTerm)
  const [mergeFieldStore] = useState(
    () =>
      new MergeFieldsStore(
        {
          onAddMergeField: (field: MergeField) => insertMergeFields(field),
          contact: conversationContact,
          isSelectDefault: true,
        },
        entities
      )
  )

  const isGridView = selectedViewType === 'grid'

  useEffect(() => {
    const debouncedSetSearchTerm = debounce(setSearchTerm, 300)
    debouncedSetSearchTerm(inputSearchTerm)

    return () => {
      debouncedSetSearchTerm.cancel()
    }
  }, [inputSearchTerm])

  useEffect(() => {
    if (isSearchResettingForUpload) {
      setSearchTerm('')
      setIsSearchOpen(false)
    }
  }, [isSearchResettingForUpload])

  const handleUploadClick = () => {
    if (ref.current) {
      ref.current.value = ''
      ref.current.click()
    }
  }

  const onUploadChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files

    if (files) {
      void mediaLibraryStore.addFilesToLibrary(Array.from(files))
    }
  }

  const insertMergeFields = (field: MergeField) => {
    const url = textRef?.current?.value || ''

    if (!isPicsnippetsUrl(url)) {
      setError('Please enter a valid file URL', 'Unable to upload the file')
      return false
    }

    const key = getPicsnippetMergeFieldLabel(field.name)
    const assetUrl = new URL(url)
    if (key) {
      assetUrl.searchParams.set(key, field.value)
      setUploadUrl(assetUrl.href)
      return true
    }

    return false
  }

  const { videoAcceptString, fileAcceptString, allFilesAcceptString, audioAcceptString } =
    attachmentStore

  return (
    <div className={styles.controls}>
      <div className={styles.buttonsGroup}>
        {!isUploadViaUrlOpen && (
          <>
            <Button
              text='Upload or drop'
              icon='upload'
              disabled={isAttachmentInitListLoading}
              onClick={handleUploadClick}
            />
            <input
              type={'file'}
              className={styles.fileInput}
              ref={ref}
              accept={
                isAircallNumber && !featureFlagsStore.aircall_new_api
                  ? `${videoAcceptString},${fileAcceptString}`
                  : isRinglessSelectVariant || isAudioSelectVariant
                  ? audioAcceptString
                  : allFilesAcceptString
              }
              disabled={isAttachmentInitListLoading}
              onChange={onUploadChange}
              multiple
            />
            {!isRinglessSelectVariant && !isAudioSelectVariant && !isDefaultVariant && (
              <Button
                text='Add from URL'
                icon='link'
                contained='secondary'
                disabled={
                  isAttachmentInitListLoading ||
                  (isAircallNumber && !featureFlagsStore.aircall_new_api)
                }
                onClick={() => {
                  setIsUploadViaUrlOpen(true)
                  setIsSearchOpen(false)
                  setInputSearchTerm('')
                  setSearchTerm('')
                }}
              />
            )}
          </>
        )}
        {isUploadViaUrlOpen && (
          <TextField
            size='extraSmall'
            variant='outline'
            value={uploadUrl}
            onChange={setUploadUrl}
            InputProps={{ placeholder: 'Add or paste a file URL', ref: textRef }}
            disabled={uploading}
            leftActions={[
              makeTextFieldIcon({
                icon: 'link',
                color: 'var(--content-primary-tertiary)',
                fontSize: 16,
                margin: '0 4px',
                hidden: isConversationMessageField,
              }),
            ]}
            rightActions={[
              makeTextFieldElement({
                element: isConversationMessageField ? (
                  <>
                    <MergeFieldUrlDropdown mergeFieldStore={mergeFieldStore} />
                    <div className={styles.divider} />
                  </>
                ) : null,
              }),
              () => (
                <Button
                  typeBtn='text'
                  size='small'
                  text='Cancel'
                  disabled={uploading}
                  onClick={() => {
                    setUploadUrl('')
                    setIsUploadViaUrlOpen(false)
                  }}
                />
              ),
              () => (
                <Button
                  size='small'
                  text='Upload'
                  disabled={!uploadUrl || uploading}
                  onClick={() => addAttachmentViaUrl(uploadUrl)}
                />
              ),
            ]}
            mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
            className={classNames(styles.textInput, styles.uploadViaUrl, {
              [styles.textFieldIndent]: isConversationMessageField,
            })}
          />
        )}
        {isRinglessSelectVariant || isAudioSelectVariant ? (
          <RinglessSelectInfoTooltip withDurationLimit={isAudioDurationLimitEnabled} />
        ) : (
          !isUploadViaUrlOpen && (
            <SizeLimitsTooltip
              isAircall={isAircallNumber}
              isCentered
              hideURLUpload={isDefaultVariant}
            />
          )
        )}
      </div>
      <div className={styles.buttonsGroup}>
        <Tooltip label={isGridView ? 'Show as list' : 'Show as grid'} placement={'top'}>
          <IconButton
            icon={isGridView ? 'listLayout' : 'dotsGrid'}
            color='secondary'
            ariaLabel='viewType'
            disabled={isAttachmentInitListLoading}
            onClick={toggleViewType}
          />
        </Tooltip>
        {isSearchOpen ? (
          <TextField
            size='extraSmall'
            variant='outline'
            value={inputSearchTerm}
            onChange={setInputSearchTerm}
            InputProps={{ placeholder: 'Search files' }}
            leftActions={[
              makeTextFieldIcon({
                icon: 'search',
                color: 'var(--content-primary-tertiary)',
                fontSize: 16,
                margin: '0 4px',
              }),
            ]}
            rightActions={[
              () => (
                <Button
                  typeBtn='text'
                  size='small'
                  text='Cancel'
                  onClick={() => {
                    setSearchTerm('')
                    setInputSearchTerm('')
                    setIsSearchOpen(false)
                  }}
                />
              ),
            ]}
            mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
            className={classNames(styles.textInput, styles.search)}
          />
        ) : (
          <Tooltip label={'Search'} placement={'top'}>
            <IconButton
              icon='search'
              color='secondary'
              ariaLabel='search'
              disabled={isAttachmentInitListLoading || isAnyFileWaitingForUpload}
              onClick={() => {
                setIsSearchOpen(true)
                setIsUploadViaUrlOpen(false)
                setUploadUrl('')
              }}
            />
          </Tooltip>
        )}
      </div>
    </div>
  )
})
